"use client"

import { useState, useContext, useEffect, useMemo } from "react"
import { ErpContext } from "../../context/ErpContext.js"
import { getLocaleConfig, formatInputWithSeparators, parseLocalizedNumber } from "../../utils/numeros.js"

import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  Autocomplete,
  Modal,
  Divider,
  InputAdornment,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Tooltip,
  useTheme,
  alpha,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material"

// Material UI Icons
import {
  Close as CloseIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Delete as DeleteIcon,
  Payments as PaymentsIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Category as CategoryIcon,
} from "@mui/icons-material"

const ModalMedioDePago = ({
  open,
  handleClose,
  precioTotal = null,
  transaccionSeleccionada = null,
  onConfirmarPago,
  filtrarPorCrearEnCuentaCorriente = false,
  mediosDePagoIniciales = null,
  exigirTotalExacto = false,
  idUbicacionInicial = null,
  requiereSubcategoriaParaAdelanto = false,
  excluirCuentaCorriente = false,
}) => {
  // precioTotal=null significa monto libre (sin tope), ej. adelanto a proveedor
  const montoLibre = precioTotal === null || precioTotal === undefined
  const { 
    getMedioDePagoIdTipoEspecifico, 
    medioDePagoIdTipoEspecifico, 
    tipoMedioDePago, 
    getTipoMedioDePago, 
    formatoNumericoGlobal,
    ubicaciones,
    getUbicaciones,
    getParametrosGlobales,
    subCategoriaTransaccion,
    getSubCategoriaTransaccion,
    categoriaTransaccion,
    getCategoriaTransaccion,
  } = useContext(ErpContext)

  const theme = useTheme()

  // Configuración de locale para formateo numérico
  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  )

  // ✅ Detectar si es un pago de cuota (tiene cuotaNro o importeAPagar)
  const esPagoCuota = transaccionSeleccionada?.cuotaNro !== undefined || transaccionSeleccionada?.importeAPagar !== undefined
  const requiereTotalExacto = esPagoCuota || exigirTotalExacto

  // Filtrar tipos de medios de pago si se requiere
  let tiposMedioDePagoFiltrados = filtrarPorCrearEnCuentaCorriente
    ? tipoMedioDePago.filter(tipo => tipo.crearEnCuentaCorriente === true)
    : tipoMedioDePago

  // Excluir Cuenta Corriente si se requiere (para reversiones de cuotas)
  if (excluirCuentaCorriente) {
    tiposMedioDePagoFiltrados = tiposMedioDePagoFiltrados.filter(
      tipo => tipo.descripcion !== 'Cuenta Corriente'
    )
  }

  // Estados para medios de pago
  const [modalMedioDePagoSeleccionadoID, setModalMedioDePagoSeleccionadoID] = useState(null)
  const [modalTipoMedioDePagoSeleccionado, setModalTipoMedioDePagoSeleccionado] = useState(null)
  const [modalMedioDePagoSeleccionado, setModalMedioDePagoSeleccionado] = useState(null)
  const [modalMedioDePagoCantidad, setModalMedioDePagoCantidad] = useState(null)
  const [modalMedioDePagoCantidadDisplay, setModalMedioDePagoCantidadDisplay] = useState("")
  const [medioDePagoList, setMedioDePagoList] = useState([])
  const [cantidaMedioDePagoList, setCantidaMedioDePagoList] = useState(0)
  const [selectedTipoMedioDePago, setSelectedTipoMedioDePago] = useState(null)
  const [selectedMedioDePago, setSelectedMedioDePago] = useState([])
  const [openCollapse, setOpenCollapse] = useState(false)
  const [mostrarButtonModalMedioDePago, setMostrarButtonModalMedioDePago] = useState(false)
  const [medioDePagoEspecificoSeleccionadoModal, setMedioDePagoEspecificoSeleccionadoModal] = useState(null)
  const [descripcionPago, setDescripcionPago] = useState("")
  const [subcategoriaSeleccionada, setSubcategoriaSeleccionada] = useState(null)
  
  // Estados para ubicación
  const [ubicacionSeleccionada, setUbicacionSeleccionada] = useState(null)
  const [ubicacionDefaultCargada, setUbicacionDefaultCargada] = useState(false)

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getTipoMedioDePago()
        await getUbicaciones()
      } catch (error) {
        console.error("Error fetching data:", error)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    if (open && requiereSubcategoriaParaAdelanto && getSubCategoriaTransaccion && getCategoriaTransaccion) {
      getSubCategoriaTransaccion()
      getCategoriaTransaccion()
    }
  }, [open, requiereSubcategoriaParaAdelanto])

  useEffect(() => {
    if (!open) return
    const iniciales = Array.isArray(mediosDePagoIniciales) ? mediosDePagoIniciales : []
    setMedioDePagoList(iniciales)
    const totalInicial = iniciales.reduce((total, item) => total + (Number(item.monto) || 0), 0)
    setCantidaMedioDePagoList(totalInicial)
    
    // Cargar ubicación por defecto solo una vez al abrir el modal
    if (!ubicacionDefaultCargada) {
      cargarUbicacionDefault()
    }
  }, [open, mediosDePagoIniciales])
  
  // Cargar ubicación por defecto desde parámetros globales
  const cargarUbicacionDefault = async () => {
    try {
      // Si viene una ubicación inicial, usarla
      if (idUbicacionInicial) {
        const ubicacionInicial = ubicacionesNoDestino.find(u => u.id === idUbicacionInicial)
        if (ubicacionInicial) {
          setUbicacionSeleccionada(ubicacionInicial)
          setUbicacionDefaultCargada(true)
          return
        }
      }
      
      // Si no, buscar el parámetro global
      const parametros = await getParametrosGlobales(['idUbicacionDefaultTransaccionCaja'])
      const idUbicacionDefault = parametros?.idUbicacionDefaultTransaccionCaja
      
      if (idUbicacionDefault) {
        const ubicacionDefault = ubicacionesNoDestino.find(u => u.id === parseInt(idUbicacionDefault))
        if (ubicacionDefault) {
          setUbicacionSeleccionada(ubicacionDefault)
          setUbicacionDefaultCargada(true)
        }
      }
    } catch (error) {
      console.error("Error cargando ubicación por defecto:", error)
    }
  }
  
  // Filtrar ubicaciones que NO son destinos (esDestino = false)
  const ubicacionesNoDestino = useMemo(() => {
    return (ubicaciones || []).filter(u => u.esDestino === false)
  }, [ubicaciones])
  
  // Resetear flag cuando se cierra el modal
  useEffect(() => {
    if (!open) {
      setUbicacionDefaultCargada(false)
    }
  }, [open])

  // Agregar nuevo useEffect para cargar medios específicos
  useEffect(() => {
    const fetchMediosEspecificos = async () => {
      if (modalTipoMedioDePagoSeleccionado?.id) {
        try {
          await getMedioDePagoIdTipoEspecifico(modalTipoMedioDePagoSeleccionado)
        } catch (error) {
          console.error("Error fetching medios específicos:", error)
        }
      }
    }

    fetchMediosEspecificos()
  }, [modalTipoMedioDePagoSeleccionado])

  // Limpiar selección cuando cambia el filtro
  useEffect(() => {
    if (modalTipoMedioDePagoSeleccionado && filtrarPorCrearEnCuentaCorriente) {
      // Verificar si el tipo seleccionado tiene crearEnCuentaCorriente = true
      if (modalTipoMedioDePagoSeleccionado.crearEnCuentaCorriente !== true) {
        setModalTipoMedioDePagoSeleccionado(null)
        setModalMedioDePagoSeleccionado(null)
        setModalMedioDePagoSeleccionadoID(null)
      }
    }
  }, [filtrarPorCrearEnCuentaCorriente, modalTipoMedioDePagoSeleccionado])

  // Función para agregar nuevo medio de pago
  const handleAddNuevoMedioDePago = () => {
    if (modalMedioDePagoSeleccionado && modalMedioDePagoCantidad) {
      const yaExiste = medioDePagoList.some((mp) => mp.id === modalMedioDePagoSeleccionadoID)
      if (yaExiste) {
        showSnackbar("Este medio de pago ya ha sido seleccionado", "warning")
        return
      }

      const montoAgregar = Number.parseFloat(modalMedioDePagoCantidad)
      const nuevoTotal = cantidaMedioDePagoList + montoAgregar

      // ✅ Si es pago de cuota, validar que el monto sea exactamente igual
      if (esPagoCuota) {
        if (Math.abs(nuevoTotal - precioTotal) > 0.01) {
          const restante = precioTotal - cantidaMedioDePagoList
          const mensajeError = nuevoTotal > precioTotal
            ? `El monto excede la cuota pactada. Debe ser exactamente ${new Intl.NumberFormat("es-AR", {
                style: "currency",
                currency: "ARS",
              }).format(precioTotal)}. Use el botón "Restante" para autocompletar el monto exacto (${new Intl.NumberFormat("es-AR", {
                style: "currency",
                currency: "ARS",
              }).format(restante)}).`
            : `El monto es inferior a la cuota pactada. Debe ser exactamente ${new Intl.NumberFormat("es-AR", {
                style: "currency",
                currency: "ARS",
              }).format(precioTotal)}. Use el botón "Restante" para autocompletar el monto exacto (${new Intl.NumberFormat("es-AR", {
                style: "currency",
                currency: "ARS",
              }).format(restante)}).`
          showSnackbar(mensajeError, "error")
          return
        }
      } else if (!montoLibre) {
        // ✅ Validar que no se exceda el monto total a pagar (para pagos normales con tope)
        if (nuevoTotal > precioTotal) {
          const restante = precioTotal - cantidaMedioDePagoList
          showSnackbar(
            `No puede agregar más del monto adeudado. Restante: ${new Intl.NumberFormat("es-AR", {
              style: "currency",
              currency: "ARS",
            }).format(restante)}`,
            "error"
          )
          return
        }
      }

      const newMedioDePago = {
        id: modalMedioDePagoSeleccionadoID,
        idPago: null,
        descripcion: modalMedioDePagoSeleccionado,
        monto: montoAgregar,
        tipoMedioDePago: modalTipoMedioDePagoSeleccionado.id,
      }

      const nuevaLista = [...medioDePagoList, newMedioDePago]
      setMedioDePagoList(nuevaLista)

      const updatedTotalMedioPagoList = nuevaLista.reduce((total, item) => total + item.monto, 0)
      setCantidaMedioDePagoList(updatedTotalMedioPagoList)

      setModalMedioDePagoSeleccionado("")
      setModalMedioDePagoSeleccionadoID(null)
      setModalMedioDePagoCantidad("")
      setModalMedioDePagoCantidadDisplay("")

      showSnackbar("Medio de pago agregado", "success")
    } else {
      showSnackbar("Debe seleccionar un medio de pago y especificar un monto", "warning")
    }
  }

  // Función para mostrar snackbar
  const showSnackbar = (message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    })
  }

  // ✅ Función para autocompletar el saldo restante
  const handlePagarRestante = () => {
    const restante = precioTotal - cantidaMedioDePagoList
    if (restante > 0) {
      setModalMedioDePagoCantidad(restante)
      setModalMedioDePagoCantidadDisplay(formatInputWithSeparators(restante, localeConfig.locale))
      showSnackbar("Monto restante autocompletado", "success")
    } else {
      showSnackbar("No hay saldo restante por pagar", "info")
    }
  }

  // Función para eliminar medio de pago
  const handleDeleteMedioDePago = (index, medioDePagoMonto) => {
    setCantidaMedioDePagoList((prevTotal) => prevTotal - medioDePagoMonto)
    setMedioDePagoList(medioDePagoList.filter((_, i) => i !== index))

    showSnackbar("Medio de pago eliminado", "info")
  }

  // Función para resetear completamente el modal
  const resetModal = () => {
    setModalMedioDePagoSeleccionadoID(null)
    setModalTipoMedioDePagoSeleccionado(null)
    setModalMedioDePagoSeleccionado(null)
    setModalMedioDePagoCantidad(null)
    setModalMedioDePagoCantidadDisplay("")
    setMedioDePagoList([])
    setCantidaMedioDePagoList(0)
    setSelectedTipoMedioDePago(null)
    setSelectedMedioDePago([])
    setOpenCollapse(false)
    setMostrarButtonModalMedioDePago(false)
    setMedioDePagoEspecificoSeleccionadoModal(null)
    setDescripcionPago("")
    setSubcategoriaSeleccionada(null)
    setUbicacionSeleccionada(null)
    setUbicacionDefaultCargada(false)
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
            <PaymentsIcon sx={{ mr: 1, color: "rgb(244, 147, 14)" }} />
            Selección de Medios de Pago
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <Autocomplete
              size="small"
              fullWidth
              options={ubicacionesNoDestino}
              getOptionLabel={(option) => option.descripcion || ""}
              value={ubicacionSeleccionada}
              onChange={(event, newValue) => setUbicacionSeleccionada(newValue)}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Ubicación" 
                  required
                  error={!ubicacionSeleccionada}
                  helperText={!ubicacionSeleccionada ? "Debe seleccionar una ubicación" : ""}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              size="small"
              fullWidth
              options={tiposMedioDePagoFiltrados}
              getOptionLabel={(option) => option.descripcion || ""}
              value={modalTipoMedioDePagoSeleccionado}
              onChange={(event, newValue) => setModalTipoMedioDePagoSeleccionado(newValue)}
              renderInput={(params) => <TextField {...params} label="Tipo de medio de pago" />}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Autocomplete
              size="small"
              fullWidth
              options={medioDePagoIdTipoEspecifico.map((it) => ({
                label: it.descripcion,
                id: it.id,
              }))}
              value={modalMedioDePagoSeleccionado}
              onChange={(event, newValue) => {
                if (newValue) {
                  setModalMedioDePagoSeleccionado(newValue.label)
                  setModalMedioDePagoSeleccionadoID(newValue.id)
                } else {
                  setModalMedioDePagoSeleccionado("")
                  setModalMedioDePagoSeleccionadoID(null)
                }
              }}
              renderInput={(params) => <TextField {...params} label="Medio de Pago" />}
            />
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 3,
            gap: 1,
            backgroundColor: "transparent",
          }}
        >
          <TextField
            label="Monto a pagar"
            size="small"
            type="text"
            inputMode="decimal"
            sx={{ flex: "1 1 auto", minWidth: 150, maxWidth: 220 }}
            value={modalMedioDePagoCantidadDisplay}
            onChange={(e) => {
              const inputValue = e.target.value;
              const { locale, decimalSeparator, thousandSeparator } = localeConfig;
              
              if (inputValue === "") {
                setModalMedioDePagoCantidad("");
                setModalMedioDePagoCantidadDisplay("");
                return;
              }
              
              const parsed = parseLocalizedNumber(inputValue, locale);
              if (parsed === null && inputValue.trim() !== "") return;
              
              let formatted = parsed !== null ? formatInputWithSeparators(parsed, locale) : "";
              
              // Preservar decimal al final
              if (inputValue.endsWith(decimalSeparator) && !formatted.endsWith(decimalSeparator)) {
                formatted += decimalSeparator;
              }
              
              setModalMedioDePagoCantidad(parsed ?? "");
              setModalMedioDePagoCantidadDisplay(formatted);
            }}
            onBlur={(e) => {
              const parsed = parseLocalizedNumber(e.target.value, localeConfig.locale);
              if (parsed !== null && parsed >= 0) {
                setModalMedioDePagoCantidadDisplay(formatInputWithSeparators(parsed, localeConfig.locale));
              }
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{
              autoComplete: "off",
            }}
          />
          <Tooltip
            title={requiereTotalExacto ? `El monto debe ser exactamente ${new Intl.NumberFormat("es-AR", {
              style: "currency",
              currency: "ARS",
            }).format(precioTotal)}. Use este botón para autocompletar el monto exacto.` : "Autocompletar el monto restante"}
            arrow
            enterDelay={1000}
          >
            <span>
              <Button
                variant="outlined"
                onClick={handlePagarRestante}
                startIcon={<AccountBalanceWalletIcon />}
                disabled={montoLibre ? true : precioTotal - cantidaMedioDePagoList <= 0}
                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
              >
                Restante
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            onClick={handleAddNuevoMedioDePago}
            startIcon={<AddCircleOutlineIcon />}
            disabled={
              !modalMedioDePagoSeleccionado ||
              !modalMedioDePagoCantidad ||
              (!montoLibre && (esPagoCuota
                ? Math.abs(cantidaMedioDePagoList + Number.parseFloat(modalMedioDePagoCantidad || 0) - precioTotal) > 0.01
                : cantidaMedioDePagoList + Number.parseFloat(modalMedioDePagoCantidad || 0) > precioTotal))
            }
            sx={{ flexShrink: 0 }}
          >
            Agregar
          </Button>
        </Box>

        {requiereSubcategoriaParaAdelanto &&
        (medioDePagoList.some((m) => m.tipoMedioDePago !== 5) ||
          (modalTipoMedioDePagoSeleccionado && modalTipoMedioDePagoSeleccionado.id !== 5)) && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: "flex", alignItems: "center", fontWeight: 600 }}>
              <CategoryIcon sx={{ mr: 1, color: "rgb(244, 147, 14)", fontSize: 18 }} />
              Subcategoría
            </Typography>
            <FormControl fullWidth required size="small">
              <InputLabel id="modal-subcategoria-label">Subcategoría</InputLabel>
              <Select
                labelId="modal-subcategoria-label"
                label="Subcategoría"
                value={subcategoriaSeleccionada?.id || ""}
                onChange={(e) => {
                  const id = e.target.value
                  const sub = (subCategoriaTransaccion || []).find((s) => s.id === id)
                  setSubcategoriaSeleccionada(sub || null)
                }}
              >
                <MenuItem value="">
                  <em>Seleccionar subcategoría</em>
                </MenuItem>
                {(subCategoriaTransaccion || []).map((sub) => (
                  <MenuItem key={sub.id} value={sub.id}>
                    {sub.descripcion}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {subcategoriaSeleccionada && categoriaTransaccion?.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  size="small"
                  label={`Categoría: ${(categoriaTransaccion || []).find((c) => c.id === subcategoriaSeleccionada.idCategoriaTransaccion)?.descripcion || "-"}`}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        )}

        <Card variant="outlined" sx={{ mb: 3, bgcolor: alpha(theme.palette.primary.light, 0.05) }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
              Resumen de Pagos
            </Typography>

            {!montoLibre && (
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body1">Total a pagar:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {new Intl.NumberFormat("es-AR", {
                    style: "currency",
                    currency: "ARS",
                  }).format(precioTotal)}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body1">Total medios de pago:</Typography>
              <Typography variant="body1" fontWeight="bold">
                {new Intl.NumberFormat("es-AR", {
                  style: "currency",
                  currency: "ARS",
                }).format(cantidaMedioDePagoList)}
              </Typography>
            </Box>

            {!montoLibre && (
              <>
                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body1">Diferencia:</Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color={precioTotal === cantidaMedioDePagoList ? "success.main" : "error.main"}
                  >
                    {new Intl.NumberFormat("es-AR", {
                      style: "currency",
                      currency: "ARS",
                    }).format(precioTotal - cantidaMedioDePagoList)}
                  </Typography>
                </Box>

                {precioTotal !== cantidaMedioDePagoList && cantidaMedioDePagoList > 0 && (
                  <Alert severity={cantidaMedioDePagoList < precioTotal ? "info" : "warning"} sx={{ mt: 2 }}>
                    {cantidaMedioDePagoList < precioTotal
                      ? `Pago parcial: Se pagará $${cantidaMedioDePagoList.toLocaleString("es-AR")} de $${precioTotal.toLocaleString("es-AR")}. Saldo restante: $${(precioTotal - cantidaMedioDePagoList).toLocaleString("es-AR")}`
                      : "El total de medios de pago es mayor al saldo a pagar"}
                  </Alert>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Box sx={{ mb: 3 }}>
          <TextField
            label="Descripción del pago (opcional)"
            multiline
            rows={3}
            fullWidth
            value={descripcionPago}
            onChange={(e) => setDescripcionPago(e.target.value)}
            placeholder="Ej: Pago de factura #123, Abono a cuenta, Cancelación de deuda, etc."
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
            helperText="Agregue una descripción para identificar este pago"
          />
        </Box>

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Medios de Pago Seleccionados
        </Typography>

        {medioDePagoList.length > 0 ? (
          <Box sx={{ maxHeight: 200, overflow: "auto", mb: 2 }}>
            {medioDePagoList.map((it, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1,
                  mb: 1,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.background.default, 0.7),
                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                }}
              >
                <Typography variant="body2">{it.descripcion}</Typography>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    {new Intl.NumberFormat("es-AR", {
                      style: "currency",
                      currency: "ARS",
                    }).format(it.monto)}
                  </Typography>
                  <IconButton size="small" color="error" onClick={() => handleDeleteMedioDePago(index, it.monto)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: "center", py: 2 }}>
            No hay medios de pago seleccionados
          </Typography>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (onConfirmarPago) {
                onConfirmarPago(
                  medioDePagoList,
                  cantidaMedioDePagoList,
                  transaccionSeleccionada,
                  descripcionPago,
                  ubicacionSeleccionada?.id,
                  subcategoriaSeleccionada?.id
                )
                resetModal()
              } else {
                handleClose()
              }
            }}
            disabled={
              !ubicacionSeleccionada ||
              medioDePagoList.length === 0 ||
              cantidaMedioDePagoList <= 0 ||
              (requiereTotalExacto ? Math.abs(cantidaMedioDePagoList - precioTotal) > 0.01 : false) ||
              (requiereSubcategoriaParaAdelanto &&
              (medioDePagoList.some((m) => m.tipoMedioDePago !== 5) ||
                (modalTipoMedioDePagoSeleccionado && modalTipoMedioDePagoSeleccionado.id !== 5)) &&
              !subcategoriaSeleccionada)
            }
          >
            Confirmar
          </Button>
        </Box>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Modal>
  )
}

export default ModalMedioDePago
